// Loading whichever messenger SDK we are actually running inside.
//
// The SDK is only needed for chrome — ready()/expand(), fullscreen, the back
// button, theme variables. Authentication deliberately does not go through it
// (see detect.ts), so a slow or unreachable CDN degrades the frame, never the
// login.
//
// Which platform we are on is decided by the *backend*, from the signature on
// the launch payload — no client-side sniffing, and nothing here can be fooled
// by a page pretending to be a different messenger.

import { restoreLaunchHash } from './detect'

export type MiniappPlatform = 'bale' | 'telegram' | 'eitaa'

interface SdkSpec {
    url: string
    global: string
    /**
     * Whether to ask the client for true fullscreen.
     *
     * Off everywhere by product decision: the app is designed for its normal
     * size and looks worse stretched. Eitaa and Telegram (8.0+) do support it;
     * Bale has no such call at all. Kept as a flag rather than deleted so
     * turning it back on is one edit.
     */
    supportsFullscreen: boolean
    /**
     * Whether requestContact() can end up at our BOT webhook.
     *
     * Bale and Telegram post the shared contact into the bot chat, which
     * reaches us server-to-server — so it is worth waiting a few seconds for
     * that to land. Eitaa has no webhook at all, so waiting there is
     * guaranteed-useless dead time and must be skipped.
     */
    deliversContactToBot: boolean
    /**
     * Whether to call expand() on launch.
     *
     * Off for Bale by product decision: its expanded sheet stretches the app
     * past the size it is designed for and looks worse than the default.
     */
    expandOnLaunch: boolean
}

const SDKS: Record<MiniappPlatform, SdkSpec> = {
    bale: {
        url: 'https://tapi.bale.ai/miniapp.js?3',
        global: 'Bale',
        // Bale's bundle has expand() and nothing else — no requestFullscreen,
        // no viewport height, no safe-area API. Verified against the shipped
        // file, not the docs.
        supportsFullscreen: false,
        deliversContactToBot: true,
        expandOnLaunch: false,
    },
    telegram: {
        url: 'https://telegram.org/js/telegram-web-app.js',
        global: 'Telegram',
        // Telegram added requestFullscreen in Bot API 8.0; older clients simply
        // ignore the call, which the try/catch below already tolerates.
        supportsFullscreen: false,
        deliversContactToBot: true,
        expandOnLaunch: true,
    },
    eitaa: {
        url: 'https://developer.eitaa.com/eitaa-web-app.js',
        global: 'Eitaa',
        supportsFullscreen: false,
        // No webhook, no getUpdates — nothing can ever arrive server-to-server.
        deliversContactToBot: false,
        expandOnLaunch: true,
    },
}

const LOAD_TIMEOUT_MS = 5000

export interface MessengerWebApp {
    initData: string
    initDataUnsafe: Record<string, unknown>
    version: string
    colorScheme: 'light' | 'dark'
    themeParams: Record<string, string>
    ready: () => void
    expand: () => void
    close: () => void
    setHeaderColor: (color: string) => void
    requestFullscreen?: () => void
    exitFullscreen?: () => void
    addToHomeScreen?: () => void
    requestContact?: (
        cb: (shared: boolean, phoneNumber?: string) => void
    ) => void
    checkHomeScreenStatus?: (cb: (result: { status?: string }) => void) => void
    BackButton: {
        isVisible: boolean
        show: () => void
        hide: () => void
        onClick: (cb: () => void) => void
        offClick: (cb: () => void) => void
    }
}

declare global {
    interface Window {
        Bale?: { WebApp: MessengerWebApp }
        Telegram?: { WebApp: MessengerWebApp }
        Eitaa?: { WebApp: MessengerWebApp }
    }
}

const pending: Partial<Record<MiniappPlatform, Promise<MessengerWebApp | null>>> = {}

const readGlobal = (name: string): MessengerWebApp | null => {
    const holder = (window as unknown as Record<string, { WebApp?: MessengerWebApp }>)[name]
    return holder?.WebApp ?? null
}

export const deliversContactToBot = (platform: MiniappPlatform): boolean =>
    !!SDKS[platform]?.deliversContactToBot

export const loadSdk = (platform: MiniappPlatform): Promise<MessengerWebApp | null> => {
    const spec = SDKS[platform]
    if (!spec) return Promise.resolve(null)

    // The SDK parses location.hash exactly once, as it loads. Routing has very
    // likely replaced the URL since launch, so put the launch fragment back
    // first — otherwise the SDK initialises with no version and refuses its own
    // version-gated methods.
    restoreLaunchHash()

    const existing = readGlobal(spec.global)
    if (existing) return Promise.resolve(existing)
    if (pending[platform]) return pending[platform] as Promise<MessengerWebApp | null>

    pending[platform] = new Promise<MessengerWebApp | null>((resolve) => {
        let settled = false
        const finish = (value: MessengerWebApp | null) => {
            if (settled) return
            settled = true
            resolve(value)
        }

        const script = document.createElement('script')
        script.src = spec.url
        script.async = true
        script.onload = () => finish(readGlobal(spec.global))
        script.onerror = () => finish(null)
        document.head.appendChild(script)

        // Never let a hanging CDN hold the app hostage.
        window.setTimeout(() => finish(readGlobal(spec.global)), LOAD_TIMEOUT_MS)
    })

    return pending[platform] as Promise<MessengerWebApp | null>
}

// `header-bg` from tailwind.config.js — the pale green of the app's own top
// bar. Painting the messenger's header the same colour removes the seam
// between its chrome and ours.
const APP_HEADER_COLOR = '#E3F1D9'

/**
 * Tell the client we have painted, take as much of the screen as it allows,
 * and blend its header into ours.
 *
 * Without ready() the client keeps its own loading spinner over us.
 *
 * Fullscreen is requested only where it exists. Bale has no such call at all,
 * so there its title bar is simply a fact of life; Telegram (8.0+) and Eitaa
 * both support it, and an older client that doesn't just throws, which the
 * catch swallows.
 */
export const announceReady = async (platform: MiniappPlatform) => {
    const webApp = await loadSdk(platform)
    if (!webApp) return
    try {
        webApp.ready()
        if (SDKS[platform]?.expandOnLaunch) {
            webApp.expand()
        }
        webApp.setHeaderColor(APP_HEADER_COLOR)
    } catch {
        // A client older than these methods is still perfectly usable.
    }
    if (SDKS[platform]?.supportsFullscreen) {
        try {
            webApp.requestFullscreen?.()
        } catch {
            // Not supported by this client version — expand() already applied.
        }
    }
}
